import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
} from '@mui/material';
import { API_CONFIG, API_ENDPOINTS, STORAGE_KEYS } from '../helpers/constants';
import { capitalizeFirstLetter } from '../helpers/stringUtils';

interface WorkspaceTag {
  id: string;
  name: string;
  description: string;
  channel_count: number;
  created_at: string;
}

interface WorkspaceTagOption extends WorkspaceTag {
  isSpecial: boolean;
}

interface SpecialTagOption {
  id: string;
  name: string;
  isSpecial: true;
}

interface TagsResponse {
  tags: WorkspaceTag[];
  total: number;
  size: number;
}

interface WorkspaceTagAutocompleteProps {
  value?: string | null;
  onChange: (value: { id: string | null; name: string | null } | null) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  size?: 'small' | 'medium';
  showAddNew?: boolean;
  onAddNew?: () => void;
  showAllOption?: boolean;
  allOptionLabel?: string;
  fullWidth?: boolean;
  /** When true, dropdown is forced closed (e.g. when a sibling modal like Connect WhatsApp is open) */
  forceCloseWhen?: boolean;
  /** Optional ref that receives { blur } so parent can blur the input (e.g. when opening a sibling modal). Use this instead of ref so the forwarded ref can stay on the root DOM for Tooltip. */
  blurRef?: React.RefObject<WorkspaceTagAutocompleteHandle | null>;
  'data-testid'?: string;
  sx?: any;
}

export interface WorkspaceTagAutocompleteHandle {
  /** Blur the workspace input (e.g. when opening a sibling modal so the field does not stay focused) */
  blur: () => void;
}

const WorkspaceTagAutocomplete = forwardRef<HTMLDivElement, WorkspaceTagAutocompleteProps>(({
  value,
  onChange,
  label = 'Workspace',
  placeholder,
  disabled = false,
  required = false,
  size = 'medium',
  showAddNew = false,
  onAddNew,
  showAllOption = false,
  allOptionLabel = 'All Workspaces',
  fullWidth = false,
  forceCloseWhen = false,
  blurRef,
  'data-testid': testId,
  sx,
}, ref) => {
  const [workspaceTags, setWorkspaceTags] = useState<WorkspaceTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [allTagsLoaded, setAllTagsLoaded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const hasFetchedTags = useRef(false);
  // Ref to the actual input so parent can blur it when opening a sibling modal
  const inputRef = useRef<HTMLInputElement>(null);

  // Expose blur on blurRef so parent can blur the input; ref is forwarded to root DOM for Tooltip
  useImperativeHandle(blurRef, () => ({
    blur: () => {
      inputRef.current?.blur();
    },
  }), []);

  // When a sibling modal (e.g. Connect WhatsApp) opens, force-close this dropdown so it does not show behind the modal
  useEffect(() => {
    if (forceCloseWhen) {
      setIsOpen(false);
      setSearchQuery('');
    }
  }, [forceCloseWhen]);

  const fetchWorkspaceTags = useCallback(async (
    pageNum: number = 1, 
    search: string = '', 
    append: boolean = false
  ) => {
    try {
      setLoading(true);
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const pageSize = 15;
      let url = `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.TAGS(pageNum, pageSize)}`;
      
      // Try adding search query if provided (backend may or may not support it)
      if (search.trim()) {
        url += `&search=${encodeURIComponent(search.trim())}`;
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: TagsResponse = await response.json();
      let newTags = data.tags || [];
      
      // If backend doesn't support search, do client-side filtering
      if (search.trim() && newTags.length > 0) {
        const searchLower = search.toLowerCase();
        newTags = newTags.filter(tag => 
          tag.name.toLowerCase().includes(searchLower)
        );
      }
      
      if (append) {
        setWorkspaceTags(prev => [...prev, ...newTags]);
      } else {
        setWorkspaceTags(newTags);
      }
      
      // Check if there are more tags to load
      setHasMore(newTags.length === pageSize && !search.trim());
      setAllTagsLoaded(newTags.length < pageSize || !!search.trim());
    } catch (err) {
      console.error('Failed to fetch workspace tags:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    if (!hasFetchedTags.current) {
      hasFetchedTags.current = true;
      fetchWorkspaceTags(1, '');
    }
  }, [fetchWorkspaceTags]);

  // Handle search query changes with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (isOpen) { // Only search when dropdown is open
        if (searchQuery !== '') {
          setPage(1);
          fetchWorkspaceTags(1, searchQuery, false);
        } else if (hasFetchedTags.current) {
          setPage(1);
          fetchWorkspaceTags(1, '', false);
        }
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, isOpen, fetchWorkspaceTags]);

  const loadMoreTags = () => {
    if (hasMore && !loading && !allTagsLoaded) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchWorkspaceTags(nextPage, searchQuery, true);
    }
  };

  const handleChange = (event: any, newValue: WorkspaceTagOption | SpecialTagOption | null) => {
    if (newValue) {
      if (newValue.isSpecial) {
        if (newValue.id === 'all') {
          onChange({ id: 'all', name: allOptionLabel });
        } else if (newValue.id === 'add-new' && onAddNew) {
          onAddNew();
        }
      } else {
        onChange({ id: newValue.id, name: newValue.name });
      }
    } else {
      // If cleared and showAllOption is true, default back to "all"
      if (showAllOption) {
        onChange({ id: 'all', name: allOptionLabel });
      } else {
        onChange(null);
      }
    }
  };

  // Hide "all" option when searching, so users can search and select specific tags
  const options: (WorkspaceTagOption | SpecialTagOption)[] = [
    ...(showAllOption && !searchQuery.trim() ? [{ id: 'all', name: allOptionLabel, isSpecial: true } as SpecialTagOption] : []),
    ...(showAddNew ? [{ id: 'add-new', name: '+ Add new workspace Tag', isSpecial: true } as SpecialTagOption] : []),
    ...workspaceTags.map(tag => ({ ...tag, isSpecial: false } as WorkspaceTagOption))
  ];

  const selectedValue = (() => {
    // When searching, don't show "all" as selected - allow users to see and select matching tags
    if (searchQuery.trim() && (value === 'all' || value === allOptionLabel)) {
      return null;
    }
    
    // Handle "all" option specifically - check both 'all' string and allOptionLabel
    if ((value === 'all' || value === allOptionLabel) && showAllOption) {
      return { id: 'all', name: allOptionLabel, isSpecial: true } as SpecialTagOption;
    }
    
    // Handle null/undefined value - default to "all" if showAllOption is true (but not when searching)
    if (!value || value === '') {
      return (showAllOption && !searchQuery.trim()) ? { id: 'all', name: allOptionLabel, isSpecial: true } as SpecialTagOption : null;
    }
    
    // Handle specific workspace selection
    const foundTag = workspaceTags.find(tag => tag.name === value);
    return foundTag ? { ...foundTag, isSpecial: false } as WorkspaceTagOption : null;
  })();

  const getOptionLabel = (option: WorkspaceTagOption | SpecialTagOption) => {
    if (option.isSpecial) {
      if (option.id === 'all') {
        return allOptionLabel;
      } else if (option.id === 'add-new') {
        return '+ Add new workspace';
      }
    }
    return capitalizeFirstLetter(option.name);
  };

  // Compute inputValue: show search query when open, selected value label when closed
  // When closed and no value, use empty string to allow placeholder to show
  const inputValue = isOpen 
    ? (searchQuery || '') 
    : (selectedValue 
        ? getOptionLabel(selectedValue) 
        : (value ? capitalizeFirstLetter(value) : ''));

  const autocompleteProps: any = {
    'data-testid': testId,
    fullWidth: fullWidth,
    size: size,
    options: options,
    getOptionLabel: getOptionLabel,
    value: selectedValue,
    onChange: handleChange,
    onInputChange: (event: any, newInputValue: string, reason: string) => {
      // Update search query when user is typing
      if (reason === 'input') {
        setSearchQuery(newInputValue);
      } else if (reason === 'reset' || reason === 'clear') {
        // Reset search query when dropdown closes or is cleared
        setSearchQuery('');
      }
    },
    inputValue: inputValue,
    clearOnBlur: true,
    selectOnFocus: false,
    blurOnSelect: true,
    // When forceCloseWhen is true (e.g. Connect WhatsApp modal is open), keep dropdown closed and ignore open requests
    open: forceCloseWhen ? false : isOpen,
    onOpen: () => {
      if (!forceCloseWhen) {
        setIsOpen(true);
        setSearchQuery(''); // Clear search when opening
      }
    },
    onClose: () => {
      setIsOpen(false);
      setSearchQuery(''); // Reset search when closing
    },
    freeSolo: false,
    loading: loading,
    disabled: disabled,
  };

  // Only disable clearable when "all" is selected and showAllOption is true
  if (showAllOption && selectedValue?.id === 'all') {
    autocompleteProps.disableClearable = true;
  }

  return (
    <Autocomplete<WorkspaceTagOption | SpecialTagOption>
      ref={ref}
      {...autocompleteProps}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={(el) => {
            (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
            // MUI v7: ref is on InputProps, not params.inputRef
            const pr = params.InputProps.ref;
            if (pr) {
              if (typeof pr === 'function') (pr as (instance: HTMLInputElement | null) => void)(el);
              else (pr as React.MutableRefObject<HTMLInputElement | null>).current = el;
            }
          }}
          label={label}
          placeholder={placeholder}
          required={required}
          inputProps={{
            ...params.inputProps,
            // Prevent browser autocomplete styling (e.g. light blue "recommendation" highlight on placeholder)
            autoComplete: 'off',
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          InputLabelProps={{
            ...params.InputLabelProps,
            required: false,
            sx: {
              ...((params.InputLabelProps as any)?.sx || {}),
              '& .MuiFormLabel-asterisk': {
                display: 'none',
              },
            },
          }}
        />
      )}
      ListboxProps={{
        onScroll: (event) => {
          const listboxNode = event.currentTarget;
          if (
            listboxNode.scrollTop + listboxNode.clientHeight >=
            listboxNode.scrollHeight - 1
          ) {
            loadMoreTags();
          }
        },
        style: { 
          maxHeight: '200px'
        },
        sx: {
          '& .MuiAutocomplete-option': {
            padding: '8px 16px',
            fontSize: '14px',
          }
        }
      }}
      filterOptions={(options, params) => {
        // Don't filter options since we're handling search on the server
        return options;
      }}
      noOptionsText={
        loading 
          ? "Loading..." 
          : searchQuery 
            ? "No workspaces found" 
            : "No workspaces available"
      }
      renderOption={(props, option: WorkspaceTagOption | SpecialTagOption) => (
        <li {...props} key={option.id}>
          {option.isSpecial 
            ? (option.id === 'all' ? allOptionLabel : '+ Add new workspace')
            : capitalizeFirstLetter(option.name)
          }
        </li>
      )}
      sx={{
        '& .MuiInputBase-root': {
          cursor: 'pointer',
        },
        '& .MuiAutocomplete-input': {
          cursor: 'pointer',
        },
        '& .MuiAutocomplete-endAdornment': {
          right: '9px !important',
        },
        '& .MuiAutocomplete-inputRoot': {
          maxWidth: '100%',
        },
        ...sx
      }}
    />
  );
});

WorkspaceTagAutocomplete.displayName = 'WorkspaceTagAutocomplete';

export default WorkspaceTagAutocomplete;
